import Button, { ButtonVariant } from '@atoms/Button/Button';
import Checkbox from '@atoms/Checkbox/Checkbox';
import LikeButton from '@atoms/LikeButton/LikeButton';
import Text from '@atoms/Text/Text';
import { IColourSwatchCard } from '@models/IColourSwatchCard';
import IFormValues from '@models/IFormValues';
import { IHotspotCard } from '@models/IHotspotCard';
import { IProductCard } from '@models/IProductCard';
import {
  WishlistCard,
  WishlistEntry,
  WishlistGroupType,
} from '@models/IWishlistCardSwitch';
import { IWishlistDrawerLabels } from '@models/IWishlistDrawer';
import CardTypeEnum from '@models/enums/CardTypeEnum';
import DrawerWrapper from '@molecules/DrawerWrapper/DrawerWrapper';
import { useLikedItemsStore, useWishlistStore } from '@state/WishlistStore';
import removeExtensionFromPath from '@utility/helpers/removeExtensionFromPath';
import { getItem, updateItem } from '@utility/hooks/useIndexDb';
import { useEffect, useRef, useState } from 'react';
import { SubmitHandler, UseFormRegister, useForm } from 'react-hook-form';

interface Props {
  readonly buttonVariant?: ButtonVariant['variant'];
  readonly card: WishlistCard;
  readonly cardId: string;
  readonly collection: CardTypeEnum;
  readonly isCard?: boolean;
  readonly wishlistDrawerLabels?: IWishlistDrawerLabels;
  readonly wishlistId?: number;
}

const WishListGroup = ({
  group,
  register,
}: {
  group: WishlistGroupType;
  register: UseFormRegister<IFormValues>;
}) => (
  <>
    <Text variant="heading-21-light">{group.title}</Text>
    <ul className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-4 tw-pb-10 tw-pt-4">
      {group.options.map(({ id, label }) => (
        <Checkbox
          register={register}
          key={id}
          name={label}
          value={id}
          label={label}
        />
      ))}
    </ul>
  </>
);

const AddToWishlist = ({
  buttonVariant,
  card,
  cardId,
  collection,
  isCard = true,
  wishlistDrawerLabels,
  wishlistId,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [existingWishlistNames, setExistingWishlistNames] = useState<
    { id: number; label: string }[]
  >([]);
  const [isValid, setIsValid] = useState(false);
  const { register, handleSubmit, reset, getValues } = useForm<IFormValues>();
  const { addLikedItem, fetchLikedItems, removeLikedItem } =
    useLikedItemsStore();
  const { addWishlist, fetchWishlists, wishlists } = useWishlistStore();
  const [isLiked, setIsLiked] = useState(false);
  const hasMounted = useRef(false); // Track if the component has mounted

  useEffect(() => {
    const checkIfLiked = async () => {
      try {
        const allLikedItems = await getItem('jotun-deco', 'likes');
        const formattedId = removeExtensionFromPath(cardId);

        const likedItem = allLikedItems.find((item: WishlistEntry) => {
          // Added to support existing code
          if (item.entity.data?.linkUrl) {
            return (
              removeExtensionFromPath(item.entity.data.linkUrl) === formattedId
            );
          }
          if (item.entity.fields?.linkUrl) {
            return (
              removeExtensionFromPath(item.entity.fields.linkUrl) ===
              formattedId
            );
          } else {
            return removeExtensionFromPath(item.entity.id) === formattedId;
          }
        });

        if (likedItem) {
          setIsLiked(true);
        } else {
          setIsLiked(false);
        }
      } catch (error) {
        console.error('Error checking liked item in IndexedDB:', error);
      }
    };

    checkIfLiked();
  }, [cardId]);

  useEffect(() => {
    if (isOpen && wishlists?.length > 0) {
      const updatedWishlistNames = wishlists.map(
        ({ name, id }: { name: string; id: number }) => ({
          id: id || 0,
          label: name,
        }),
      );
      setExistingWishlistNames(updatedWishlistNames);
    }
  }, [isOpen]);

  useEffect(() => {
    if (hasMounted.current && !isOpen) {
      reset(); // Reset the form when the drawer closes
      setIsValid(false);
    } else {
      hasMounted.current = true; // Mark that the component has mounted
    }
  }, [isOpen, reset]);

  const handleLike = async () => {
    if (isLiked) {
      try {
        await removeLikedItem(cardId, wishlistId);
        await fetchLikedItems();
        await fetchWishlists();
      } catch (error) {
        console.error('Error removing liked item from IndexedDB:', error);
      } finally {
        setIsLiked(false);
      }
    } else {
      setIsOpen(true);
    }
  };

  // return form data as an array of numbers
  const normalizeFormData = (data: IFormValues) => {
    const normalizedData: Record<string, number[]> = {};

    Object.entries(data).forEach(([key, value]) => {
      if (!!value === false) {
        normalizedData[key] = [];
      } else if (typeof value === 'string') {
        normalizedData[key] = [parseInt(value, 10)];
      } else {
        normalizedData[key] = (value as string[]).map((id) => parseInt(id, 10));
      }
    });

    return normalizedData;
  };

  const onSubmit: SubmitHandler<IFormValues> = async (data) => {
    const normalizedData = normalizeFormData(data);

    const likeData = {
      entity: {
        id: cardId,
        data: card as WishlistCard,
        collection,
        newModel: true,
      },
    };

    try {
      // Get id of liked item from index
      const likeItemId = await addLikedItem(likeData);

      const allWishlists = await getItem('jotun-deco', 'lists');

      for (const [label, wishlistIds] of Object.entries(normalizedData)) {
        for (const wishlistId of wishlistIds) {
          const existingListData = allWishlists.find(
            (list: any) => list.id === wishlistId,
          );

          if (existingListData) {
            existingListData.likes.push(likeItemId);
            await updateItem(existingListData, 'jotun-deco', 'lists');
          } else {
            const listData = {
              name: label,
              likes: [likeItemId],
              // Generate random 6 digit key
              id: Math.floor(Math.random() * 1000000),
            };
            await addWishlist(listData);
          }
        }
      }

      await fetchLikedItems();
      await fetchWishlists();
    } catch (error) {
      console.error('Error storing data in IndexedDB:', error);
    } finally {
      setIsLiked(true);
      setIsOpen(false);
      handleTracking();
    }
  };

  const handleTracking = () => {
    let data = null;

    switch (collection) {
      case CardTypeEnum.Colours:
        const { colourName, colourHexCode } = card as IColourSwatchCard;

        data = {
          item_name: colourName || null,
          item_category: collection,
          item_category2: null,
          item_category3: colourHexCode || null,
        };
        break;

      case CardTypeEnum.Products:
        const { productName, category } = card as IProductCard;

        data = {
          item_name: productName || null,
          item_category: collection,
          item_category2: category || null,
          item_category3: null,
        };
        break;

      case CardTypeEnum.Inspirations:
        const { hotspots, mobileImage } = card as IHotspotCard;

        const hotspotProduct = hotspots?.[0]?.product?.name;
        const hotspotColour = hotspots?.[0]?.colour?.name;

        data = {
          item_name: hotspotProduct ? hotspotProduct : hotspotColour,
          item_category: collection,
          item_category2: hotspotProduct ? 'product' : 'colour',
          item_category3: null,
          preview_url: mobileImage?.url || null,
        };
        break;
    }

    if (data) {
      window.dataLayer.push({
        event: 'trackingEvent_add_to_wishlist',
        event_name_variable: 'add_to_wishlist',
        trackingEvent_add_to_wishlist: data,
      });
    }
  };

  const newWishlistNames = wishlistDrawerLabels?.newList.options.map(
    (name, index) => ({
      label: name,
      id: index,
    }),
  );

  return (
    <div>
      <LikeButton
        isCard={isCard}
        onLike={handleLike}
        isLiked={isLiked}
        variant={buttonVariant}
      />
      {wishlistDrawerLabels && (
        <DrawerWrapper
          title={wishlistDrawerLabels.title}
          setIsOpen={setIsOpen}
          isOpen={isOpen}
          wrapperStyles="tw-h-full tw-flex tw-flex-col"
        >
          <form
            onSubmit={handleSubmit(onSubmit)}
            onChange={() => {
              const formObject = getValues();

              const hasCheckedInputs =
                Object.values(formObject).filter(Boolean).length;
              setIsValid(!!hasCheckedInputs);
            }}
            className="tw-flex tw-h-full tw-flex-col"
          >
            <div className="tw-px-4 tw-py-10 md:tw-p-12">
              {!!existingWishlistNames.length && (
                <WishListGroup
                  group={{
                    title: wishlistDrawerLabels.existingListTitle,
                    options: existingWishlistNames,
                  }}
                  register={register}
                />
              )}
              <WishListGroup
                group={{
                  title: wishlistDrawerLabels.newList.title,
                  options: newWishlistNames || [],
                }}
                register={register}
              />
            </div>
            <div className="tw-sticky tw-bottom-0 tw-left-0 tw-mt-auto tw-flex tw-w-full">
              <Button
                disabled={!isValid}
                variant="grey80"
                label={wishlistDrawerLabels.submitLabel}
                type="submit"
                buttonClasses="tw-w-full tw-rounded-none"
              />
            </div>
          </form>
        </DrawerWrapper>
      )}
    </div>
  );
};

export default AddToWishlist;
